import React, { createContext, FC, ReactNode, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import * as FullStory from '@fullstory/browser';
import { selectCurrentEmployeeOrg, selectCurrentEmployeeRole, selectCurrentEmployeeSite } from 'store/Employers';
import { selectCurrentUser } from 'store/User';
import { IdentityType, SiteRole } from 'types';
import { getIdentityByType } from 'utils/identity';

const FULLSTORY_ORG_ID = '10F5QX';

interface IProps {
  children: ReactNode;
}

interface IFullStoryContext {
  sessionUrl?: string;
  settings?: object;
  trackEvent: (event: IFullStoryEvent) => void;
}

interface IFullStoryEvent {
  name: string;
  properties: object;
}

const FullStoryContext = createContext<IFullStoryContext | null>(null);

export const useFullStory = () => useContext(FullStoryContext);

export const FullStoryProvider: FC<IProps> = ({ children }) => {
  const user = useSelector(selectCurrentUser());
  const currentOrganization = useSelector(selectCurrentEmployeeOrg);
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const currentEmployeeRole = useSelector(selectCurrentEmployeeRole);

  const [fullStory, setFullStory] = useState<{ sessionUrl?: string; settings?: object }>({});

  // Initialize FullStory (if not already initialized)
  useEffect(() => {
    if (!FullStory.isInitialized()) FullStory.init({ orgId: FULLSTORY_ORG_ID, startCaptureManually: true }, setFullStory);
  }, []);

  // Start FullStory session for Admins and Managers, and observe the session URL
  useEffect(() => {
    if (!fullStory.sessionUrl && currentEmployeeRole && window.FS) {
      window.FS('start');
      window.FS('observe', { type: 'start', callback: () => setFullStory((prev) => ({ ...prev, sessionUrl: window.FS('getSession', { format: 'url' }) })) });
    }
  }, [currentEmployeeRole, fullStory.sessionUrl]);

  // Shutdown FullStory when user is not present (e.g., logout)
  useEffect(() => {
    if (fullStory.sessionUrl && !user && window.FS) window.FS('shutdown');
  }, [user, fullStory.sessionUrl]);

  // Set FullStory identity when the user is available
  useEffect(() => {
    if (user && window.FS) {
      const currentEmailIdentity = getIdentityByType(user.identities, IdentityType.EMAIL)?.value;
      window.FS('setIdentity', {
        uid: user.id,
        properties: {
          displayName: `${user.firstName} ${user.lastName}`,
          ...(currentEmailIdentity && { email: currentEmailIdentity }),
        },
      });
    }
  }, [user]);

  // Track "Profile Switch" event on role change or when switching organization/site
  useEffect(() => {
    if (fullStory.sessionUrl && window.FS) {
      trackFullStoryEvent({
        name: 'Profile Switch',
        properties: {
          role: currentEmployeeRole || SiteRole.EMPLOYEE,
          ...(currentOrganization && { organizationId: currentOrganization.id, organization: currentOrganization.name }),
          ...(currentSite && { siteId: currentSite.id, site: currentSite.name }),
        },
      });
    }
  }, [currentEmployeeRole, currentOrganization, currentSite, fullStory.sessionUrl]);

  // Send events to FullStory
  const trackFullStoryEvent = (event: IFullStoryEvent) => {
    if (window.FS) window.FS('trackEvent', event);
  };

  return <FullStoryContext.Provider value={{ ...fullStory, trackEvent: trackFullStoryEvent }}>{children}</FullStoryContext.Provider>;
};
